import { ContentfulLink } from 'libs/contentful/types';
import { getRelativePath, isSubdomainSlug } from 'utils/linkHelpers';
import { NavSubLinkProps, NavSubMenuProps } from './types';

export const HEADER_NAV_ID_PREFIX = 'header_nav';

export const navSubMenuEyebrowClassName =
  'font-sans mb-2 font-semibold text-[length:var(--text-label-saans)] leading-[var(--heading-sm--line-height)] tracking-[var(--heading-sm--letter-spacing)] md:font-[670] md:text-[length:var(--heading-sm)]';

export const navTopBarProps = { 'data-nav-top-bar': true } as const;
export const navTopBarSelector = '[data-nav-top-bar]';

const CHIME_SUBDOMAIN = /(?<=\/)[A-Za-z0-9]*[^www](?=\.chime)/;

const extractInternalSubdomain = (s: string) => {
  const found = s.match(CHIME_SUBDOMAIN);
  return found ? found[0] : '';
};

export const getIdFromUrl = (s: string) => {
  return s.replaceAll(/^\/|\/$/g, '').replaceAll(/[-/]/g, '_');
};

const isRelativeLink = (s: string) => {
  return s.startsWith('/') || s.startsWith('#');
};

export const generateIdSegmentFromLink = (link: ContentfulLink | undefined | null) => {
  if (!link) return '';
  if (link.internalPage != null) {
    const { slug } = link.internalPage;
    if (!slug) return '';

    // Adjust slug for enterprise subdomain
    const adjustedSlug = isSubdomainSlug(slug) ? getRelativePath(slug) : slug;

    return getIdFromUrl(adjustedSlug);
  }

  const url = link.url;
  if (!url) return '';

  // Relative link from same subdomain
  if (isRelativeLink(url)) {
    return getIdFromUrl(url);
  }

  // Non-contentful subdomains (investors, careers, etc) - Use only the subdomain in the id
  const subdomain = extractInternalSubdomain(url);
  if (subdomain.length) {
    return subdomain;
  }

  // www.chime.com links - Use only the pathname
  try {
    const urlObj = new URL(url);
    const path = urlObj.pathname;

    if (path === '/') return `chime_home`;

    return getIdFromUrl(path);
  } catch (e) {
    console.error(e);
    return '';
  }
};

export const generateNavId = (link: ContentfulLink | undefined | null) => {
  const idSegment = generateIdSegmentFromLink(link);

  return idSegment ? `${HEADER_NAV_ID_PREFIX}_${idSegment}` : '';
};

export const subLinkHasNestedItems = (subLink: NavSubLinkProps | null | undefined): boolean =>
  (subLink?.nestedItemsCollection?.items?.length ?? 0) > 0;

export const subMenuHasNestedItems = (subMenu: NavSubMenuProps | undefined): boolean =>
  subMenu?.sublinksCollection?.items?.some(subLinkHasNestedItems) ?? false;

export const getFirstNestedSubLink = (subMenu: NavSubMenuProps): NavSubLinkProps | null => {
  const items = subMenu.sublinksCollection?.items ?? [];
  return items.find(subLinkHasNestedItems) ?? null;
};
